import * as React from 'react';
import {Mutation} from 'react-apollo';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {ActionBox} from 'shells/action-box/action-box';
import {ActionMenu} from 'shells/action-menu/action-menu';
import {Button} from 'shells/button/button';
import {ButtonGroup} from 'shells/button-group/button-group';
import {Card} from 'shells/card/card';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {EmailIcon, ErrorCircleIcon} from 'shells/icon';
import {SpacerGroup} from 'shells/spacer/spacer-group';

import type {
    UserEmail,
    DisableSmtpConnection as DisableSmtpConnectionMutation,
    DisableSmtpConnectionVariables,
    SetSmtpMailSendIsEnabled as SetSmtpMailSendIsEnabledMutation,
    SetSmtpMailSendIsEnabledVariables as SetSmtpMailSendIsEnabledMutationVariables,
} from 'nutshell-graphql-types';

import DISABLE_SMTP_CONNECTION from '../graphql/mutations/disable-smtp-connection.graphql';
import SET_SMTP_MAIL_SEND_IS_ENABLED from '../graphql/mutations/set-smtp-mail-send-is-enabled.graphql';
import {getSettingsFromUserEmail} from '../smtp/get-settings-from-user-email';
import {SmtpForm} from '../smtp/smtp-form';
import type {ExposedSubform} from '../smtp/smtp-form';
import {SubmitSmtp} from '../smtp/submit-smtp';

import styles from './email-widget-smtp.module.css';

const smtpIcon = <EmailIcon wrapWithDiv={false} width={24} />;

type Props = {
    email: UserEmail;
    isVerificationPollTimedOut: boolean;
    retryVerification: () => Promise<any>;
};

class SmtpConnectionDisable extends Mutation<
    DisableSmtpConnectionMutation,
    DisableSmtpConnectionVariables
> {}

class SmtpSendIsEnabled extends Mutation<
    SetSmtpMailSendIsEnabledMutation,
    SetSmtpMailSendIsEnabledMutationVariables
> {}

export function EmailWidgetSmtp(props: Props) {
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const formRef = React.useRef<ExposedSubform | null | undefined>({handleSubmit: () => {}});

    // This is a little roundabout, but it causes the SmtpForm's `onSubmit` to be called
    // with the new values it is tracking in its state.
    function subformSubmit() {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    }
    const currentSettings = getSettingsFromUserEmail(props.email);

    ReactTooltip.rebuild();

    return isEditing ? (
        <Card hasBorder={true}>
            <div className='flex flex-dir-col pad-16'>
                {errorMessage ? (
                    <SpacerGroup>
                        <FlashBanner hasBorder={true} type='error'>
                            {errorMessage}
                        </FlashBanner>
                    </SpacerGroup>
                ) : null}
                <SubmitSmtp
                    onVerificationTimeout={() => {
                        setErrorMessage(
                            'It’s taking longer than expected to verify. Check back in a few minutes, or try updating your email settings.'
                        );
                    }}
                    onVerificationFailure={(userEmail: UserEmail) => {
                        const msg =
                            (userEmail &&
                                userEmail.smtpCredentials &&
                                userEmail.smtpCredentials.verificationMessage) ||
                            '';
                        setErrorMessage(
                            `Unable to connect, please try again. ${msg ? `(${msg})` : ''}`
                        );
                    }}
                    onError={(msg) => {
                        setErrorMessage(msg || 'Unable to connect, please try again.');
                    }}
                    onVerificationSuccess={() => {
                        setIsEditing(false);
                    }}
                    smtpSettings={currentSettings}
                >
                    {({updateAndTest, inProgress}) => {
                        // updateAndTest will do the mutations and error handling
                        // @ts-expect-error
                        function handleSubmit(newSettings) {
                            setErrorMessage(null);
                            updateAndTest(newSettings);
                        }

                        return (
                            <React.Fragment>
                                <SmtpForm
                                    // @ts-expect-error
                                    ref={formRef}
                                    disabled={inProgress}
                                    userEmailId={props.email.id}
                                    hostname={currentSettings.hostname}
                                    username={currentSettings.username}
                                    port={currentSettings.port}
                                    portType={currentSettings.ssl}
                                    password={currentSettings.password}
                                    onCancelEdit={() => setIsEditing(false)}
                                    onSubmit={handleSubmit}
                                />
                                <div className='flex justify-end'>
                                    <ButtonGroup>
                                        <Button
                                            variant='text-secondary'
                                            size='big'
                                            onClick={() => setIsEditing(false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant='primary'
                                            onClick={subformSubmit}
                                            size='big'
                                            disabled={inProgress}
                                        >
                                            {inProgress ? 'Connecting…' : 'Connect and test'}
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            </React.Fragment>
                        );
                    }}
                </SubmitSmtp>
            </div>
        </Card>
    ) : (
        <ActionBox
            title={props.email.address}
            subtitle={getSmtpEmailStatus(
                props.email,
                props.isVerificationPollTimedOut,
                props.retryVerification
            )}
            icon={smtpIcon}
            actionElement={
                <div className='flex align-center'>
                    {getActionButton(props.email, () => setIsEditing(true))}
                    {getActionMenu(props.email, () => setIsEditing(true))}
                </div>
            }
        />
    );
}

function getSmtpEmailStatus(
    email: UserEmail,
    isTimedOut: boolean,
    retryVerification: () => Promise<any>
): React.ReactElement<any> {
    if (!email.smtpCredentials) {
        return <div>Not configured</div>;
    }

    switch (email.smtpCredentials.verificationStatus) {
        case 'UNVERIFIED':
            return isTimedOut ? (
                <div className={styles.warning}>
                    Unable to verify
                    <Button variant='text' size='big' onClick={retryVerification}>
                        Retry
                    </Button>
                </div>
            ) : (
                <div>Verifying…</div>
            );
        case 'VERIFIED':
            return <div>SMTP settings verified</div>;
        case 'FAILED':
            return (
                <div
                    data-tip={email.smtpCredentials.verificationMessage}
                    data-delay-show='0'
                    data-place='bottom'
                    className={styles.error}
                >
                    Failed
                    <ErrorCircleIcon />
                </div>
            );
    }

    return <div>Enabled</div>;
}

function getActionButton(email: UserEmail, onEditSettings: () => void): React.ReactElement<any> {
    const editButton = (
        <Button variant='text-primary' size='xlarge' onClick={onEditSettings}>
            Edit settings
        </Button>
    );

    if (!email.smtpCredentials) {
        // SMTP is not configured
        return editButton;
    }

    if (email.smtpCredentials.verificationStatus === 'VERIFIED') {
        // Sending enabled, show disable button
        return (
            <SmtpSendIsEnabled mutation={SET_SMTP_MAIL_SEND_IS_ENABLED}>
                {(setSmtpMailSendIsEnabled, {loading: isDisabling}) => {
                    let buttonText = '';
                    if (email.isEnabledForSending) {
                        buttonText = isDisabling ? 'Disabling…' : 'Disable';
                    } else {
                        buttonText = isDisabling ? 'Enabling…' : 'Enable';
                    }

                    return (
                        <Button
                            variant={email.isEnabledForSending ? 'text-danger' : 'primary'}
                            size='xlarge'
                            disabled={isDisabling}
                            onClick={() => {
                                setSmtpMailSendIsEnabled({
                                    variables: {
                                        userEmailId: email.id,
                                        isEnabled: !email.isEnabledForSending,
                                    },
                                });
                            }}
                        >
                            {buttonText}
                        </Button>
                    );
                }}
            </SmtpSendIsEnabled>
        );
    }

    if (email.smtpCredentials.verificationStatus === 'FAILED') {
        return (
            <Button variant='text-primary' size='xlarge' onClick={onEditSettings}>
                Fix settings
            </Button>
        );
    }

    return editButton;
}

function getActionMenu(email: UserEmail, onEditSettings: () => void): React.ReactElement<any> {
    return (
        <SmtpConnectionDisable mutation={DISABLE_SMTP_CONNECTION}>
            {(smtpConnectionDisable) => {
                const removeAccountAction = {
                    label: 'Remove account',
                    handleSelect: () => {
                        smtpConnectionDisable({
                            variables: {
                                userEmailId: email.id,
                            },
                        });
                    },
                } as const;

                const editSettingsAction = {
                    label: 'Edit settings',
                    handleSelect: onEditSettings,
                } as const;

                if (!email.smtpCredentials) {
                    // SMTP is not configured
                    return <ActionMenu options={[removeAccountAction]} />;
                }

                if (
                    email.smtpCredentials.verificationStatus === 'VERIFIED' &&
                    email.isEnabledForSending
                ) {
                    // SMTP is verified and sending is enabled
                    return <ActionMenu options={[editSettingsAction]} />;
                } else if (email.smtpCredentials.verificationStatus === 'VERIFIED') {
                    // SMTP is verified, and sending is not enabled
                    return <ActionMenu options={[editSettingsAction, removeAccountAction]} />;
                }

                return <ActionMenu options={[removeAccountAction]} />;
            }}
        </SmtpConnectionDisable>
    );
}
